import { isEmpty } from 'lodash';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { Jurisdiction } from '/@/modules/predefined/models/Jurisdiction';
import { Country } from '/@/modules/predefined/models/Country';
import { County } from '/@/modules/predefined/models/County';
import { AddressType } from '/@/modules/address/models/AddressType';
import { GooglePlaceDetails } from '/@/modules/core/geo/models/GooglePlaceDetails';
import CountryService from '/@/modules/predefined/services/CountryService';
import JurisdictionService from '/@/modules/predefined/services/JurisdictionService';
import CountyService from '/@/modules/predefined/services/CountyService';

export class Address extends BaseEntityModel {
  id?: number;
  modelType?: string;
  modelId?: number;
  streetName?: string;
  aptNumber?: string;
  streetNumber?: string;
  postalCode?: string;
  city?: string;
  countyId?: number | null;
  jurisdictionId?: number;
  countryId?: number;
  fullAddress?: string;
  occupiedSince?: string;
  administrativeDivision?: string;
  jurisdiction: Jurisdiction | null;
  addressTypes: Array<AddressType> | null;
  country: Country | null;
  county: County | null;

  constructor(model: Partial<Address> = {}) {
    super(model);

    this.id = model.id;
    this.modelType = model.modelType;
    this.modelId = model.modelId;
    this.streetName = model.streetName;
    this.aptNumber = model.aptNumber;
    this.streetNumber = model.streetNumber;
    this.postalCode = model.postalCode;
    this.city = model.city;
    this.countyId = model.countyId;
    this.countryId = model.countryId;
    this.jurisdictionId = model.jurisdictionId;
    this.fullAddress = model.fullAddress;
    this.occupiedSince = model.occupiedSince;
    this.administrativeDivision = model.administrativeDivision;

    this.jurisdiction = Jurisdiction.mapModelOrNull(model.jurisdiction);
    this.country = Country.mapModelOrNull(model.country);
    this.county = County.mapModelOrNull(model.county);
    this.addressTypes = AddressType.mapModelsOrNull(model.addressTypes);
  }

  getModelName(): ModelType {
    return ModelType.ADDRESS;
  }

  async setFromGooglePlaceDetails(placeDetails: GooglePlaceDetails): Promise<void> {
    this.aptNumber = placeDetails.aptNumber;
    this.streetNumber = placeDetails.streetNumber ?? '';
    this.streetName = placeDetails.streetAddress;
    this.city = placeDetails.city;
    this.postalCode = placeDetails.postalCode;
    this.jurisdictionId =
      JurisdictionService.getJurisdictionByName(placeDetails.jurisdiction)?.id ?? null;
    this.countryId = CountryService.getCountryByName(placeDetails.country)?.id ?? null;
    this.fullAddress = placeDetails.fullAddress;
    this.administrativeDivision = placeDetails.jurisdiction;
    await this.setCountyId(placeDetails);
  }

  async setCountyId(placeDetails: GooglePlaceDetails): Promise<void> {
    if (this.canSetCounty(placeDetails) && this.jurisdictionId) {
      this.countyId =
        (
          await CountyService.getCountyByNameAndJurisdiction(
            placeDetails.county,
            this.jurisdictionId
          )
        )?.id ?? null;
    } else {
      this.countyId = null;
    }
  }

  canSetCounty(placeDetails: GooglePlaceDetails): boolean {
    return (
      !!this.jurisdictionId &&
      !!placeDetails.county &&
      JurisdictionService.isUSJurisdiction(this.jurisdictionId)
    );
  }

  get shortAddress(): string {
    if (!this.streetNumber || this.streetName?.startsWith(this.streetNumber)) {
      return `${this.streetName}, ${this.city}`;
    }
    return `${this.streetNumber} ${this.streetName}, ${this.city}`;
  }

  get cityProvinceCountryAddress(): string {
    if (!this.jurisdictionId) {
      return this.fullAddress ?? '';
    }

    let address: string = this.city ?? '';

    if (this.countyId) {
      address = `${address}, ${this.county?.name}`;
    }

    return `${address}, ${this.getJurisdictionName()}, ${this.getCountryName()}`;
  }

  get businessName(): string | undefined {
    return this.fullAddress?.split(',')[0] ?? this.streetName;
  }

  get isComplete(): boolean {
    return (
      !isEmpty(this.streetName) &&
      !isEmpty(this.city) &&
      !!this.countryId &&
      (!!this.jurisdictionId || !isEmpty(this.administrativeDivision)) &&
      !isEmpty(this.postalCode)
    );
  }

  setFullAddress(): void {
    this.fullAddress = [
      this.isStreetNumberPresentInStreetName() ? '' : this.streetNumber,
      this.streetName,
      this.aptNumber ? `Apt ${this.aptNumber}` : '',
      this.city,
      this.getJurisdictionName(),
      this.getCountryName(),
      this.postalCode
    ]
      .filter((item: string | undefined) => !!item)
      .join(', ');
  }

  isStreetNumberPresentInStreetName(): boolean {
    return !!(this.streetNumber && this.streetName?.startsWith(this.streetNumber));
  }

  getJurisdictionName(): string {
    return JurisdictionService.getJurisdictionById(this.jurisdictionId)?.name ?? '';
  }

  getCountryName(): string {
    return CountryService.getActiveCountryById(this.countryId)?.name ?? '';
  }

  hasFullAddress(): boolean {
    return this.fullAddress?.length > 0;
  }
}
